/**
 * Suplidores CXP — pantalla unificada de suplidores y sus consultas.
 *
 * La vista principal es el maestro de suplidores embebido (ares8505:
 * listado con alta/edición en diálogo). Tarjetas de acción:
 *   - "Estado de cuenta" (ares8535): facturas pendientes del suplidor con
 *     antigüedad a la fecha de corte (diálogo con barra Volver).
 *   - "Antigüedad de saldo" (ares8530): balances pendientes por buckets
 *     0-30/31-60/61-90/+90 (diálogo con barra Volver).
 *
 * Sustituye las entradas de menú de suplidorescxp.js,
 * estadocuentasuplidor.js y antiguedadsaldocxp.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import SuplidoresCxp from "modules/cuentasxpagar/suplidorescxp";
import EstadoCuentaSuplidor from "modules/cuentasxpagar/estadocuentasuplidor";
import AntiguedadSaldoCxp from "modules/cuentasxpagar/antiguedadsaldocxp";
import useAccionInicial from "lib/useAccionInicial";

export default function CxpSuplidores() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeSuplidores = tienePermisosModulo(roleDetalles, "ares8505");
  const puedeEstado = tienePermisosModulo(roleDetalles, "ares8535");
  const puedeAntiguedad = tienePermisosModulo(roleDetalles, "ares8530");

  const [estadoOpen, setEstadoOpen] = useState(false);
  const [antiguedadOpen, setAntiguedadOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    estado: { permitido: puedeEstado, abrir: () => setEstadoOpen(true) },
    antiguedad: { permitido: puedeAntiguedad, abrir: () => setAntiguedadOpen(true) },
  });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Suplidores"
        subtitle="Suplidores de cuentas por pagar, con estado de cuenta y antigüedad de saldo"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeEstado || puedeAntiguedad) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeEstado && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={AccountBalanceWalletOutlinedIcon}
                  titulo="Estado de cuenta"
                  descripcion="Facturas pendientes del suplidor a la fecha de corte"
                  color={FP.color.accentBlue}
                  onClick={() => setEstadoOpen(true)}
                />
              </Grid>
            )}
            {puedeAntiguedad && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={HourglassBottomOutlinedIcon}
                  titulo="Antigüedad de saldo"
                  descripcion="Balances pendientes por antigüedad 0-30 / 31-60 / 61-90 / +90"
                  color={FP.color.info}
                  onClick={() => setAntiguedadOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: maestro de suplidores (alta/edición) */}
        {puedeSuplidores && <SuplidoresCxp embedded />}
      </Box>

      {/* Estado de cuenta (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={estadoOpen} onClose={() => setEstadoOpen(false)}>
        <BarraVolver onExit={() => setEstadoOpen(false)} />
        <EstadoCuentaSuplidor />
      </Dialog>

      {/* Antigüedad de saldo (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={antiguedadOpen} onClose={() => setAntiguedadOpen(false)}>
        <BarraVolver onExit={() => setAntiguedadOpen(false)} />
        <AntiguedadSaldoCxp />
      </Dialog>
    </Box>
  );
}
